import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { FacetSearchPageData } from '@occ/api-client';
import type { AxfoodStoreInfoViewModel } from '@occ/api-client';
import useCustomer from '@hooks/useCustomer';
import { trackLoadMore } from '@helpers/analyticsHelpers/trackPromotionPage';
import usePromotionCampaigns from './usePromotionCampaigns';

const usePromotionOfferList = (
  isPreview: boolean,
  selectedOfflineStore: AxfoodStoreInfoViewModel,
  activeTab?: PromotionTabType
) => {
  const searchParams = useSearchParams();
  // For the preview page the date range params drive refetches, so include
  // them in the reset dependency alongside the query.
  const dateFrom = isPreview ? searchParams?.get('from') : undefined;
  const dateTo = isPreview ? searchParams?.get('to') : undefined;
  const { customer } = useCustomer();
  const [page, setPage] = useState(0);
  const [showProductsWithoutImages, setShowProductsWithoutImages] = useState(false);
  const [combinedGeneralCampaigns, setCombinedGeneralCampaigns] = useState<FacetSearchPageData | null>(null);

  const { query, personalCampaigns, generalCampaigns, isLoading, error } = usePromotionCampaigns(
    isPreview,
    page,
    showProductsWithoutImages,
    selectedOfflineStore,
    activeTab
  );

  const hasMore =
    !isLoading &&
    !!combinedGeneralCampaigns?.pagination?.totalNumberOfResults &&
    !!combinedGeneralCampaigns?.results?.length &&
    combinedGeneralCampaigns.pagination.totalNumberOfResults > combinedGeneralCampaigns.results.length;

  const loadMore = (track = false) => {
    if (track) trackLoadMore(activeTab === 'butik' ? 'offline' : 'online');
    if (combinedGeneralCampaigns?.pagination?.currentPage !== undefined) {
      setPage(combinedGeneralCampaigns.pagination.currentPage + 1);
    }
  };

  // Reset only on inputs that change the campaigns request. Anything else (e.g. the active store loading
  // after the offers on the store tab, which queries the selected store) clears the offers with no
  // refetch to fill them again.
  useEffect(() => {
    setCombinedGeneralCampaigns(null);
    setPage(0);
  }, [activeTab, customer?.isAnonymous, query, dateFrom, dateTo]);

  useEffect(() => {
    if (!generalCampaigns) return;
    setCombinedGeneralCampaigns((prev) => {
      if (generalCampaigns.pagination?.currentPage === 0) return generalCampaigns;
      return {
        ...prev,
        pagination: generalCampaigns.pagination,
        results: [...(prev?.results || []), ...(generalCampaigns.results || [])],
      };
    });
  }, [generalCampaigns]);

  return {
    page,
    setPage,
    combinedGeneralCampaigns,
    personalCampaigns,
    isLoading,
    error,
    hasMore,
    loadMore,
    showProductsWithoutImages,
    setShowProductsWithoutImages,
  };
};

export default usePromotionOfferList;
